import React, { useState, useEffect } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { LayoutDashboard, MessageSquare, ListChecks, User, Shield, Briefcase, Mic, Sparkles, Calculator, Hammer } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from '../hooks/useTranslation';

const Sidebar = () => {
  const location = useLocation();
  const { api, user } = useAuth();
  const { t } = useTranslation();
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    const checkAdmin = async () => {
      try {
        const res = await api.get('/api/admin/check');
        setIsAdmin(res.data.is_admin);
      } catch {
        setIsAdmin(false);
      }
    };
    checkAdmin();
  }, [api]);

  const modules = user?.modules_enabled || { team: true, cfo: true, studio: true };

  const rawNav = [
    { path: '/dashboard', icon: LayoutDashboard, labelKey: 'nav.dashboard' },
    { path: '/chat', icon: MessageSquare, labelKey: 'nav.atlas' },
    { path: '/driving', icon: Mic, labelKey: 'nav.driving' },
    { path: '/decisions', icon: ListChecks, labelKey: 'nav.tracker' },
    { path: '/business', icon: Briefcase, labelKey: 'nav.business' },
    { path: '/studio', icon: Sparkles, labelKey: 'nav.studio', module: 'studio' },
    { path: '/cfo', icon: Calculator, labelKey: 'nav.cfo', module: 'cfo' },
    { path: '/team', icon: Hammer, labelKey: 'nav.team', module: 'team' },
    { path: '/profile', icon: User, labelKey: 'nav.profile' },
  ];
  const navItems = rawNav.filter(item => !item.module || modules[item.module]);

  if (isAdmin) {
    navItems.push({ path: '/admin', icon: Shield, labelKey: 'nav.admin' });
  }

  return (
    <aside className="w-64 bg-atlasCard/30 border-r border-atlasBorder flex flex-col h-full">
      {/* Logo */}
      <div className="p-6 border-b border-atlasBorder">
        <h1 className="atlas-brand text-2xl text-atlasCyan tracking-[0.2em]">ATLAS</h1>
        <p className="text-xs text-atlasTextSecondary mt-1">{t('nav.copilot')}</p>
      </div>

      {/* Navigation */}
      <nav className="flex-1 p-4">
        <ul className="space-y-2">
          {navItems.map(({ path, icon: Icon, labelKey }) => {
            const isActive = location.pathname === path;
            const label = t(labelKey);
            return (
              <li key={path}>
                <NavLink
                  to={path}
                  data-testid={`nav-${label.toLowerCase()}`}
                  className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-all ${
                    isActive 
                      ? path === '/admin' 
                        ? 'bg-atlasYellow/10 text-atlasYellow border-l-2 border-atlasYellow'
                        : 'bg-atlasCyan/10 text-atlasCyan border-l-2 border-atlasCyan' 
                      : 'text-atlasTextSecondary hover:bg-white/5 hover:text-white'
                  }`}
                >
                  <Icon className="w-5 h-5" />
                  <span className="font-medium">{label}</span>
                </NavLink>
              </li>
            );
          })}
        </ul>
      </nav>

      {/* Footer */}
      <div className="p-4 border-t border-atlasBorder">
        <p className="text-xs text-atlasTextSecondary text-center">
          {t('nav.version')}
        </p>
      </div>
    </aside>
  );
};

export default Sidebar;
